// attribution.js — first-touch UTM attribution beacons (privacy-safe).
//
// Records anonymous funnel-stage attribution to /api/marketing/attribution.
// Stores ONLY an anonymous client id (aid) + first-touch UTM/referrer/landing
// page in a cookie, and the user's INTERNAL id after registration. NO email /
// name / phone is ever captured here. Kept entirely separate from the Meta
// Pixel / Conversions API payloads.

const API = process.env.REACT_APP_BACKEND_URL;
const AID_COOKIE = 'jtr_aid';
const FT_COOKIE = 'jtr_ft';
const SEG_COOKIE = 'jtr_seg';
const ONE_YEAR = 60 * 60 * 24 * 365;

function readCookie(name) {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name, value, maxAge = ONE_YEAR) {
  try { document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`; } catch { /* noop */ }
}

function uuid() {
  try { if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID(); } catch { /* fall through */ }
  return `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// Stable anonymous client id (1-year cookie).
function getAid() {
  let aid = readCookie(AID_COOKIE);
  if (!aid) { aid = uuid(); writeCookie(AID_COOKIE, aid); }
  return aid;
}

// First-touch UTM/referrer/landing-page, captured once and reused for every
// later stage beacon so attribution survives in-app navigation.
function getFirstTouch() {
  const existing = readCookie(FT_COOKIE);
  if (existing) {
    try { return JSON.parse(existing); } catch { /* rebuild below */ }
  }
  const p = new URLSearchParams(window.location.search);
  const ft = {
    utm_source: p.get('utm_source') || '',
    utm_medium: p.get('utm_medium') || '',
    utm_campaign: p.get('utm_campaign') || '',
    utm_content: p.get('utm_content') || '',
    utm_term: p.get('utm_term') || '',
    landing_page: window.location.pathname || '',
    referrer: document.referrer || '',
  };
  writeCookie(FT_COOKIE, JSON.stringify(ft));
  return ft;
}

// First-touch audience-segment hint from a ?seg= ad param, persisted so it
// survives in-app navigation. Backend canonicalises + applies the
// "never overwrite a known segment with Unknown" rule.
function getSeg() {
  const existing = readCookie(SEG_COOKIE);
  if (existing) return existing;
  try {
    const seg = new URLSearchParams(window.location.search).get('seg');
    if (seg) { writeCookie(SEG_COOKIE, seg); return seg; }
  } catch { /* ignore */ }
  return '';
}

// Send a funnel-stage attribution beacon. Best-effort, never throws.
export function recordAttribution(stage, extra = {}) {
  try {
    const ft = getFirstTouch();
    const body = JSON.stringify({ aid: getAid(), stage, seg: getSeg(), ...ft, ...extra });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/api/marketing/attribution`, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(`${API}/api/marketing/attribution`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true,
      }).catch(() => {});
    }
  } catch { /* attribution is best-effort */ }
}

// Fire the 'visitor' stage once per browser session.
export function captureVisitor() {
  try {
    if (sessionStorage.getItem('jtr_attr_visitor')) return;
    sessionStorage.setItem('jtr_attr_visitor', '1');
  } catch { /* sessionStorage unavailable — still beacon once */ }
  recordAttribution('visitor');
}
